import React, { useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiLink,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiTextArea,
  EuiTitle,
} from "@elastic/eui";
import { OplItem, OplMeeting, isOplItemClosed } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { fmtDate, isoDay, statusLabel, typeLabel } from "./format";

/**
 * The right column: one point in full, its log, and the decisions the round can
 * take on it.
 *
 * The log is the centre of gravity, not the fields. Fields above it are thin -
 * due date and owner - and every change to them lands in the log below, which is
 * append-only and has no edit affordance anywhere.
 */

const i18n = "anketa.opsanio.opl";

export interface PointDetailProps {
  item: OplItem;
  round: OplMeeting | null;
  /** Owner candidates across all rounds - work may go to someone not in the room. */
  people: Array<{ value: string; label: string; inRound: boolean }>;
  busy: boolean;
  logError: string | null;
  onClose: () => void;
  onPatch: (patch: { dueDate?: string; owner?: string }) => void;
  onAppendLog: (text: string) => void;
  onDone: () => void;
  onDiscard: (reason: string) => void;
  onReopen: (reason: string) => void;
  onSplit: () => void;
  onPromote: (target: "gitlab" | "otobo") => void;
  /** opsanio/opl · update: edit fields, log, close / reopen / discard. */
  canUpdate: boolean;
  /** opsanio/opl · create: split and promote to GitLab. */
  canCreate: boolean;
  /** Promote to OTOBO additionally needs opsanio/otobo · create. */
  canPromoteOtobo: boolean;
}

export const PointDetail = ({
  item,
  round,
  people,
  busy,
  logError,
  onClose,
  onPatch,
  onAppendLog,
  onDone,
  onDiscard,
  onReopen,
  onSplit,
  onPromote,
  canUpdate,
  canCreate,
  canPromoteOtobo,
}: PointDetailProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const t = (k: string, d: string, values?: Record<string, unknown>) =>
    translate(`${i18n}.${k}`, { defaultMessage: d, values });

  const [draft, setDraft] = useState("");

  // A new point gets a fresh draft: the half-typed entry belonged to the old one.
  useEffect(() => setDraft(""), [item.id]);

  const closed = isOplItemClosed(item.status);
  const isInfo = item.type === "I";
  // Without update rights the fields render read-only, like a locked point.
  const fieldsLocked = !canUpdate || (isInfo || closed);

  const ownerOptions = [
    { value: "", text: "-" },
    ...people.map((p) => ({
      value: p.value,
      text: `${p.label}${
        p.inRound ? "" : `  · ${t("notInRound", "nicht in dieser Runde")}`
      }`,
    })),
    // Keep the stored owner selectable even if they appear in no round at all.
    ...(item.owner && !people.some((p) => p.value === item.owner)
      ? [{ value: item.owner, text: item.owner }]
      : []),
  ];

  const kv = (k: string, value: React.ReactNode) => (
    <>
      <span className="oplKV__k">{k}</span>
      <span>{value}</span>
    </>
  );

  return (
    <aside className="oplBoard__detail">
      {/* --- header --- */}
      <EuiFlexGroup
        gutterSize="s"
        alignItems="center"
        responsive={false}
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--opl-line-soft)",
          flexGrow: 0,
        }}
      >
        <EuiFlexItem grow={false}>
          <span className="oplMono" style={{ fontWeight: 500 }}>
            {item.key}
          </span>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <span className={`oplType oplType--${item.type}`}>{item.type}</span>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            {typeLabel(t, item.type)} · {t("type.immutable", "Typ unveränderlich")}
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem />
        <EuiFlexItem grow={false}>
          <EuiButtonIcon
            iconType="cross"
            color="text"
            aria-label={translate("anketa.action.close", { defaultMessage: "Schließen" })}
            onClick={onClose}
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      {/* --- fields --- */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--opl-line-soft)" }}>
        <EuiTitle size="xxs">
          <h3>{item.title}</h3>
        </EuiTitle>

        <EuiSpacer size="m" />

        <div className="oplKV">
          {kv(
            t("detail.entry", "Eingang"),
            <span className="oplMono">{item.openedIn?.key ?? "-"}</span>,
          )}
          {kv(
            t("col.due", "Fällig"),
            fieldsLocked ? (
              <span className="oplMono">{fmtDate(item.dueDate)}</span>
            ) : (
              <input
                type="date"
                value={isoDay(item.dueDate)}
                onChange={(e) => onPatch({ dueDate: e.target.value })}
                disabled={busy}
                aria-label={t("col.due", "Fällig")}
                style={{
                  padding: "4px 6px",
                  border: "1px solid var(--opl-line)",
                  borderRadius: 4,
                  background: "var(--opl-card)",
                  fontSize: 12,
                }}
              />
            ),
          )}
          {kv(
            t("col.owner", "Verantwortlich"),
            fieldsLocked ? (
              item.owner ?? "-"
            ) : (
              <EuiSelect
                compressed
                value={item.owner ?? ""}
                options={ownerOptions}
                disabled={busy}
                onChange={(e) => onPatch({ owner: e.target.value })}
                aria-label={t("col.owner", "Verantwortlich")}
              />
            ),
          )}
          {kv(t("col.status", "Status"), statusLabel(t, item.status))}
          {kv(
            t("detail.closedIn", "Erledigt in Runde"),
            <span className="oplMono">{item.closedIn?.key ?? "-"}</span>,
          )}-
        </div>

        {isInfo && (
          <>
            <EuiSpacer size="s" />
            <div className="oplNote">
              {t(
                "type.hintI",
                "Typ I: Fällig und Verantwortlich bleiben leer. Wird daraus eine Aufgabe, entsteht ein neuer Punkt mit Verweis auf diese ID.",
              )}
            </div>
          </>
        )}

        {item.assignedInAbsence && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut
              size="s"
              color="warning"
              iconType="alert"
              title={t("assignedInAbsence", "in Abwesenheit zugewiesen")}
            />
          </>
        )}

        {(item.gitlabIssueUrl || item.otoboTicketId) && (
          <>
            <EuiSpacer size="s" />
            <EuiFlexGroup gutterSize="s" responsive={false} wrap>
              <EuiFlexItem grow={false}>
                <EuiText size="xs" color="subdued">
                  {t("detail.linked", "Verknüpft")}
                </EuiText>
              </EuiFlexItem>
              {item.gitlabIssueUrl && (
                <EuiFlexItem grow={false}>
                  <EuiLink href={item.gitlabIssueUrl} target="_blank" external={false}>
                    <span style={{ fontSize: 12 }}>GitLab #{item.gitlabIssueIid} ↗</span>
                  </EuiLink>
                </EuiFlexItem>
              )}
              {item.otoboTicketId && (
                <EuiFlexItem grow={false}>
                  {item.otoboTicketUrl ? (
                    <EuiLink href={item.otoboTicketUrl} target="_blank" external={false}>
                      <span style={{ fontSize: 12 }}>
                        OTOBO {item.otoboTicketNumber} ↗
                      </span>
                    </EuiLink>
                  ) : (
                    <span style={{ fontSize: 12 }}>OTOBO {item.otoboTicketNumber}</span>
                  )}
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </>
        )}
      </div>

      {/* --- log --- */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--opl-line-soft)" }}>
        <div className="oplEyebrow">{t("detail.log", "Log - wird nur ergänzt")}</div>
        <EuiSpacer size="s" />-

        {item.log.map((entry) => (
          <div className="oplLog" key={entry.id}>
            <span className="oplMono oplLog__date">{fmtDate(entry.entryDate)}</span>
            <span className="oplLog__text">{entry.text}</span>
          </div>
        ))}
        {item.log.length === 0 && (
          <EuiText size="xs" color="subdued">
            -
          </EuiText>
        )}

        {canUpdate && (
        <>
        <EuiSpacer size="s" />

        <EuiTextArea
          compressed
          fullWidth
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("detail.logPlaceholder", "Log-Eintrag für diese Runde …")}
          aria-label={t("detail.log", "Log")}
          disabled={busy || !round}
        />
        <EuiSpacer size="s" />
        <EuiFlexGroup gutterSize="s" responsive={false} wrap>
          <EuiFlexItem grow={false}>
            <EuiButton
              size="s"
              fill
              onClick={() => {
                onAppendLog(draft);
                setDraft("");
              }}
              disabled={busy || !round}
            >
              {t("detail.logAdd", "Eintrag ergänzen")}
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              size="s"
              onClick={() => onAppendLog("Keine Änderung.")}
              disabled={busy || !round}
            >
              {t("detail.noChange", "keine Änderung")}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>

        </>
        )}

        {logError && (
          <>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="danger">
              {logError}
            </EuiText>
          </>
        )}
      </div>

      {/* --- decisions --- */}
      <div style={{ padding: "14px 16px" }}>
        <div className="oplEyebrow">{t("detail.decide", "Im Meeting entscheiden")}</div>
        <EuiSpacer size="s" />

        {!canUpdate ? null : closed ? (
          <EuiFlexGroup gutterSize="s" responsive={false} wrap>
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                iconType="refresh"
                onClick={() => onReopen(draft)}
                disabled={busy || !round}
              >
                {t("detail.reopen", "Wieder öffnen")}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        ) : (
          <EuiFlexGroup gutterSize="s" responsive={false} wrap>
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                color="success"
                iconType="check"
                onClick={onDone}
                disabled={busy || !round}
              >
                {t("detail.done", "Erledigt abnehmen")}
              </EuiButton>
            </EuiFlexItem>
            {canCreate && (
            <EuiFlexItem grow={false}>
              <EuiButton size="s" onClick={onSplit} disabled={busy || !round}>
                {t("detail.split", "Aufteilen …")}
              </EuiButton>
            </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                color="danger"
                onClick={() => onDiscard(draft)}
                disabled={busy || !round}
              >
                {t("detail.discard", "Verwerfen")}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}

        <EuiSpacer size="s" />
        <div className="oplNote">
          {closed
            ? t("detail.closedNote", "Nichts wird gelöscht, IDs werden nie neu vergeben.")
            : t(
                "detail.openNote",
                "Schließen, Aufteilen und Verwerfen geschehen ausschließlich in der Runde, durch den Protokollführer. Verwerfen verlangt eine Begründung im Log.",
              )}
        </div>

        <EuiHorizontalRule margin="m" />

        <EuiFlexGroup gutterSize="s" responsive={false} wrap>
          {canCreate && !item.gitlabIssueUrl && (
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                size="xs"
                iconType="branch"
                onClick={() => onPromote("gitlab")}
                disabled={busy}
              >
                {t("detail.promoteGitlab", "Als GitLab-Issue anlegen")}
              </EuiButtonEmpty>
            </EuiFlexItem>
          )}
          {canPromoteOtobo && !item.otoboTicketId && (
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                size="xs"
                iconType="email"
                onClick={() => onPromote("otobo")}
                disabled={busy}
              >
                {t("detail.promoteOtobo", "Als OTOBO-Ticket anlegen")}
              </EuiButtonEmpty>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </div>
    </aside>
  );
};
